import React from 'react';
import PropTypes from 'prop-types';

import { DEFAULT_NODE } from '../helpContent.constants';
import hasBlock from '../utils/hasBlock';
import isHotkey from 'is-hotkey';

export const H1_BLOCK_TYPE = 'header1';
export const H2_BLOCK_TYPE = 'header2';
export const H3_BLOCK_TYPE = 'header3';
export const H4_BLOCK_TYPE = 'header4';
export const H5_BLOCK_TYPE = 'header5';
export const H6_BLOCK_TYPE = 'header6';

const HEADER_BLOCK_TYPES = [
  null,
  H1_BLOCK_TYPE,
  H2_BLOCK_TYPE,
  H3_BLOCK_TYPE,
  H4_BLOCK_TYPE,
  H5_BLOCK_TYPE,
  H6_BLOCK_TYPE,
];

const defaultHeadingStyles = {
  fontWeight: 700,
  marginBlockStart: '0px',
  marginBlockEnd: '8px',
};

const getHeaderBlockType = level => {
  if (HEADER_BLOCK_TYPES[level]) {
    return HEADER_BLOCK_TYPES[level];
  }
  throw Error('There are headers 1 through 6. You must provide correct header level.');
};

export const HeaderBlock = ({ attributes, children, node }) => {
  switch (node.type) {
    case H1_BLOCK_TYPE:
      return <h1 {...attributes}>{children}</h1>;
    case H2_BLOCK_TYPE:
      return <h2 {...attributes}>{children}</h2>;
    case H3_BLOCK_TYPE:
      return <h3 {...attributes}>{children}</h3>;
    case H4_BLOCK_TYPE:
      return <h4 {...attributes}>{children}</h4>;
    case H5_BLOCK_TYPE:
      return <h5 {...attributes}>{children}</h5>;
    case H6_BLOCK_TYPE:
      return <h6 {...attributes}>{children}</h6>;
    default:
      return null;
  }
};

HeaderBlock.propTypes = {
  children: PropTypes.node,
  attributes: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  node: PropTypes.shape({
    type: PropTypes.string,
  }),
};

const imagePlugin = () => ({
  onKeyDown: (event, editor, next) => {
    const { selection } = editor.value;
    if (isHotkey('enter', event)) {
      if (selection.isExpanded) {
        editor.delete();
      }
      if (editor.isHeader()) {
        return editor.splitBlock().setBlocks(DEFAULT_NODE);
      }
    }

    next();
  },
  renderBlock: (props, editor, next) => {
    if (HEADER_BLOCK_TYPES.indexOf(props.node.type) > 0) {
      return <HeaderBlock {...props}/>;
    }
    return next();
  },
  commands: {
    toggleHeader: (editor, level) => {
      const blockType = getHeaderBlockType(level);
      editor.setBlocks(hasBlock(editor.value, blockType) ? DEFAULT_NODE : blockType);
    },
  },
  queries: {
    isHeader: (editor, level) => editor.value.blocks.some(node => {
      if (level) {
        return hasBlock(editor.value, getHeaderBlockType(level));
      }
      return HEADER_BLOCK_TYPES.indexOf(node.type) > 0;
    }),
  },
  serialize: node => {
    if (node.type === H1_BLOCK_TYPE) {
      return (
        <h1 style={{ ...defaultHeadingStyles, fontSize: '45px' }}>
          {node.text}
        </h1>
      );
    }
    if (node.type === H2_BLOCK_TYPE) {
      return (
        <h2 style={{ ...defaultHeadingStyles, fontSize: '28px' }}>
          {node.text}
        </h2>
      );
    }
    if (node.type === H3_BLOCK_TYPE) {
      return (
        <h3 style={{ ...defaultHeadingStyles, fontSize: '22px' }}>
          {node.text}
        </h3>
      );
    }
    if (node.type === H4_BLOCK_TYPE) {
      return (
        <h4 style={{ ...defaultHeadingStyles, fontSize: '18px' }}>
          {node.text}
        </h4>
      );
    }
    if (node.type === H5_BLOCK_TYPE) {
      return (
        <h5 style={{ ...defaultHeadingStyles, fontSize: '16px' }}>
          {node.text}
        </h5>
      );
    }
    if (node.type === H6_BLOCK_TYPE) {
      return (
        <h6 style={{ ...defaultHeadingStyles, fontSize: '14px' }}>
          {node.text}
        </h6>
      );
    }
  },
});

export default imagePlugin;
